import type { UserProfile } from "@sunrun/sales-experience-shared";

const PROSPECT_NOT_QUALIFIED_FOR_CONTACTS_CODE =
  "PROSPECT_NOT_QUALIFIED_FOR_CONTACTS";

type ContactsProspectContext = {
  opportunityId?: string;
  salesAssist?: string;
};

type ContactsUnavailablePresentation = {
  title: string;
  description: string;
};

function isProspectNotQualifiedContactsError(error: any): boolean {
  return error?.name === PROSPECT_NOT_QUALIFIED_FOR_CONTACTS_CODE;
}

function getProspectUnavailablePresentation(): ContactsUnavailablePresentation {
  return {
    title: "Presentation access unavailable",
    description:
      "You do not have access to this presentation. If you believe this is an error, please contact SSD to resolve this.",
  };
}

function isProspectUnavailable({
  prospect,
  prospectError,
  prospectLoading,
}: {
  prospect?: ContactsProspectContext;
  prospectError?: unknown;
  prospectLoading?: boolean;
}): boolean {
  if (prospectLoading) {
    return false;
  }
  if (prospectError) {
    return true;
  }
  return prospect == null;
}

function getContactsUnavailablePresentation({
  prospect,
  userProfile,
  errorMessage,
}: {
  prospect?: ContactsProspectContext;
  userProfile?: UserProfile;
  errorMessage?: string;
}): ContactsUnavailablePresentation {
  if (!prospect) {
    return getProspectUnavailablePresentation();
  }

  if (!prospect.opportunityId) {
    return {
      title: "Offer presentation is not ready",
      description:
        "This Lead has not been converted to an Opportunity yet. Convert the Lead to an Opportunity before viewing the offer presentation or managing contacts.",
    };
  }

  if (userProfile?.contactId === prospect?.salesAssist) {
    return {
      title: "Contacts access unavailable",
      description:
        "You do not have access to contacts on this Opportunity. Contact SSD with the customer address so they can fix your Sales Assist sharing.",
    };
  }

  return {
    title: "Contacts unavailable",
    description: `${
      errorMessage ?? "Contacts are not available for this record right now."
    } Contact SSD with the customer address and the error details if you need help.`,
  };
}

export {
  PROSPECT_NOT_QUALIFIED_FOR_CONTACTS_CODE,
  isProspectNotQualifiedContactsError,
  getContactsUnavailablePresentation,
  getProspectUnavailablePresentation,
  isProspectUnavailable,
};
export type { ContactsUnavailablePresentation };
